import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { fetchProduct } from '@/features/catalog/products-api';
import { ProductScreen } from '@/features/catalog/product/ProductScreen';
import { Button } from '@/shared/ui/Button';
import buttonStyles from '@/shared/ui/Button.module.css';
import { EmptyState } from '@/shared/ui/EmptyState';

export interface ProductSearch {
  /** Товар только что создан копированием — об этом говорит баннер на экране. */
  copied?: boolean;
}

/**
 * Карточка созданного товара.
 *
 * Открывается в чтении, а по кнопке «Редактировать» поля становятся формой правки
 * (`PATCH /products/{id}`). Фасовки и галерея в неё не входят — у готового товара они живут
 * своими запросами, как остатки, связи и себестоимость.
 */
export const Route = createFileRoute('/_app/catalog_/products_/$productId')({
  validateSearch: (search: Record<string, unknown>): ProductSearch => ({
    copied: search.copied === true || search.copied === 'true' ? true : undefined,
  }),
  component: ProductRoute,
});

function ProductRoute() {
  const { productId } = Route.useParams();
  const { copied } = Route.useSearch();

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => fetchProduct(productId),
  });

  if (product.isPending) {
    return <div>Загружаем карточку товара…</div>;
  }

  if (product.isError) {
    return (
      <EmptyState
        icon="catalog"
        title="Карточка товара не загрузилась"
        description="Запрос к API не прошёл или товар удалили. Проверьте соединение и повторите."
        actions={
          <>
            <Button onClick={() => void product.refetch()} disabled={product.isFetching}>
              {product.isFetching ? 'Повторяем…' : 'Повторить'}
            </Button>
            <Link to="/catalog" className={`${buttonStyles.button} ${buttonStyles.ghost}`}>
              В каталог
            </Link>
          </>
        }
      />
    );
  }

  return <ProductScreen product={product.data} copied={copied === true} />;
}
